import { FC, useLayoutEffect, useRef, useState } from 'react';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/utils/tailwind';

import { OrganizationLogoImage } from '../OrganizationLogoImage/OrganizationLogoImage';

export type OrganizationSidebarBrandProps = {
  organizationName: string;
  logoUrl: string | null;
  logoAlt?: string;
  className?: string;
};

const PILL_CLASS =
  'inline-flex h-8 max-w-full min-w-0 shrink items-center rounded-full border border-[#B6E0D9] bg-[#EBF7F5] px-[10px] py-0';

export const OrganizationSidebarBrand: FC<OrganizationSidebarBrandProps> = ({
  organizationName,
  logoUrl,
  logoAlt = 'Organization logo',
  className,
}) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const displayName = organizationName.trim();

  useLayoutEffect(() => {
    if (logoUrl || !displayName) {
      setIsTruncated(false);
      return;
    }

    const el = labelRef.current;
    if (!el) return;

    const measure = () => {
      setIsTruncated(el.scrollWidth > el.clientWidth);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [displayName, logoUrl]);

  if (logoUrl) {
    return (
      <OrganizationLogoImage
        logoUrl={logoUrl}
        alt={logoAlt}
        className={cn('max-h-8 w-auto max-w-full object-contain object-left', className)}
      />
    );
  }

  if (!displayName) {
    return <span className="sr-only">Dashboard</span>;
  }

  const label = (
    <span
      ref={labelRef}
      className="min-w-0 truncate text-body-12-semibold text-[#006B5D]"
      data-testid="organization-sidebar-brand-label"
    >
      {displayName}
    </span>
  );

  const pill = (
    <div
      className={cn(PILL_CLASS, className)}
      data-testid="organization-sidebar-brand-pill"
      aria-label={isTruncated ? displayName : undefined}
    >
      {label}
    </div>
  );

  if (!isTruncated) {
    return pill;
  }

  return (
    <TooltipPrimitive.Root delayDuration={0}>
      <TooltipPrimitive.Trigger asChild>{pill}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side="bottom"
          sideOffset={6}
          className={cn(
            'z-50 max-w-[280px] rounded-lg border border-custom-warm-grey-200',
            'bg-custom-warm-grey-25 px-3 py-2 text-center text-body-14-regular text-custom-warm-grey-700 shadow-lg'
          )}
        >
          {displayName}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};
